import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDashboardStats, reloadQuestionsCache } from '../../lib/adminApi';
import { formatToIST } from '../../lib/dateUtils';

function StatsCard({ title, value, icon, color = 'blue', onClick }) {
  const colorClasses = {
    blue: 'bg-blue-600/20 text-blue-400 border-blue-500/30 hover:bg-blue-600/30',
    green: 'bg-green-600/20 text-green-400 border-green-500/30 hover:bg-green-600/30',
    purple: 'bg-purple-600/20 text-purple-400 border-purple-500/30 hover:bg-purple-600/30',
    orange: 'bg-orange-600/20 text-orange-400 border-orange-500/30 hover:bg-orange-600/30',
    pink: 'bg-pink-600/20 text-pink-400 border-pink-500/30 hover:bg-pink-600/30',
  };

  return (
    <div
      className={`p-6 rounded-lg border cursor-pointer transition-colors ${colorClasses[color] || colorClasses.blue}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
        </div>
        <div className="text-4xl opacity-80">{icon}</div>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadingCache, setReloadingCache] = useState(false);
  const [cacheMessage, setCacheMessage] = useState(null);

  // Ref to prevent double API calls in StrictMode
  const hasFetched = useRef(false);

  const handleReloadCache = async () => {
    setReloadingCache(true);
    setCacheMessage(null);
    try {
      const result = await reloadQuestionsCache();
      if (result.success) {
        setCacheMessage({ type: 'success', text: 'Cache reloaded successfully!' });
      } else {
        setCacheMessage({ type: 'error', text: result.message || 'Failed to reload cache' });
      }
    } catch (err) {
      setCacheMessage({ type: 'error', text: err.message || 'Failed to reload cache' });
    } finally {
      setReloadingCache(false);
    }
  };

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;

    async function fetchStats() {
      try {
        const data = await getDashboardStats();
        setStats(data);
      } catch (err) {
        setError(err.message || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-600/20 border border-red-500/30 rounded-lg text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        <StatsCard
          title="Total Users"
          value={stats?.users || 0}
          icon="👥"
          color="blue"
          onClick={() => navigate('/admin/users')}
        />
        <StatsCard
          title="Questions"
          value={stats?.questions || 0}
          icon="❓"
          color="green"
          onClick={() => navigate('/admin/questions')}
        />
        <StatsCard
          title="Test Cases"
          value={stats?.testCases || 0}
          icon="🧪"
          color="purple"
          onClick={() => navigate('/admin/testcases')}
        />
        <StatsCard
          title="Submissions"
          value={stats?.submissions || 0}
          icon="📝"
          color="orange"
          onClick={() => navigate('/admin/submissions')}
        />
        <StatsCard
          title="Categories"
          value={stats?.categories || 0}
          icon="🏷️"
          color="pink"
          onClick={() => navigate('/admin/categories')}
        />
      </div>

      {/* Questions per Category */}
      {stats?.categoryStats && stats.categoryStats.length > 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Questions per Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {stats.categoryStats.map((cat) => (
              <div key={cat.categoryId} className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors">
                <span className="text-gray-300">{cat.categoryName}</span>
                <span className="text-white font-semibold bg-gray-600/50 px-2 py-1 rounded">{cat.questionCount}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
          <div className="space-y-2">
            <Link
              to="/admin/questions"
              className="block p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg text-gray-300 hover:text-white transition-colors"
            >
              + Add New Question
            </Link>
            <Link
              to="/admin/import"
              className="block p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg text-gray-300 hover:text-white transition-colors"
            >
              📦 Bulk Import Questions
            </Link>
            <Link
              to="/admin/dryrun"
              className="block p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg text-gray-300 hover:text-white transition-colors"
            >
              🔬 Run Test Validation
            </Link>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">System Status</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Database</span>
              <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-sm">Connected</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Cache</span>
              <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-sm">Active</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Last Updated</span>
              <span className="text-gray-300 text-sm">{formatToIST(Date.now())}</span>
            </div>
            <div className="pt-3 border-t border-gray-700">
              <button
                onClick={handleReloadCache}
                disabled={reloadingCache}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {reloadingCache ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Reloading...
                  </>
                ) : (
                  <>
                    🔄 Reload Questions Cache
                  </>
                )}
              </button>
              {cacheMessage && (
                <p className={`mt-2 text-sm ${cacheMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {cacheMessage.text}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
